// INS's slot metadata (TargetDetails.slots[], SlotPresentation; INS 80753e780) as the C2
// controls' props, and the controls' choices back as INS Changes. A pure mapping: no slot is
// offered unless INS marks it editable, nothing is inferred from paths or values, and the
// absence values (`none`, `scalar-native`) only ever label a default; they are never sent.
import type { TargetDetails } from '../../../src/apps/selection/controller.mjs';
import type { SlotPresentation } from '../../../src/apps/selection/slot-presentation.mjs';
import type { ImageFit, ImageRatio } from '../../../src/artifacts/images/values.mjs';
import type { Change } from '../../../src/apps/selection/patch-values.mjs';
import type { SpacingSide as InsSide } from '../../../src/apps/selection/slot-presentation.mjs';
import type { NumberFormat } from '../../../src/artifacts/presentation-values.mjs';
import {
  sameFormat,
  withChoice,
  type FormatGroup,
  type FormatValueType,
} from './number-format-control';
import type {
  ColourEntry,
  PresentationChange,
  SpacingEntry,
  SpacingGroup,
  SpacingProperty,
  SpacingSide,
} from './presentation-controls';

export type DetailSlot = TargetDetails['slots'][number];

const SIDES: Record<InsSide, [SpacingProperty, SpacingSide]> = {
  marginTop: ['margin', 'top'],
  marginRight: ['margin', 'right'],
  marginBottom: ['margin', 'bottom'],
  marginLeft: ['margin', 'left'],
  paddingTop: ['padding', 'top'],
  paddingRight: ['padding', 'right'],
  paddingBottom: ['padding', 'bottom'],
  paddingLeft: ['padding', 'left'],
  rowGap: ['gap', 'row'],
  columnGap: ['gap', 'column'],
};

const editable = (slots: readonly DetailSlot[], kind: 'colour' | 'spacing' | 'numberFormat') =>
  slots.filter((s) => s.editable && s.presentation?.kind === kind && s.property === kind);

/**
 * Spacing groups for the declared sides. A group offers the steps every one of its sides
 * allows, since linked sets them all at once.
 */
export function spacingFromSlots(slots: readonly DetailSlot[]): {
  groups: SpacingGroup[];
  allowed: string[];
} {
  const groups = new Map<SpacingProperty, SpacingEntry[]>();
  let allowed: string[] | null = null;
  for (const slot of editable(slots, 'spacing')) {
    const p = slot.presentation;
    if (p?.kind !== 'spacing') continue;
    const [property, side] = SIDES[p.side];
    const present = p.presence === 'present' && typeof slot.value === 'number';
    const entry: SpacingEntry = {
      key: slot.slotId,
      side,
      value: present ? (slot.value as number) : null,
      defaultPx: p.effectiveDefault,
      clearable: slot.clearable && p.presence === 'present',
    };
    groups.set(property, [...(groups.get(property) ?? []), entry]);
    allowed = allowed
      ? allowed.filter((k) => p.allowedSpacingTokens.includes(k))
      : [...p.allowedSpacingTokens];
  }
  return {
    groups: [...groups].map(([property, entries]) => ({ property, entries })),
    allowed: allowed ?? [],
  };
}

/** One colour row per declared, editable channel slot. */
export function colourFromSlots(slots: readonly DetailSlot[]): ColourEntry[] {
  return editable(slots, 'colour').flatMap((slot) => {
    const p = slot.presentation;
    if (p?.kind !== 'colour') return [];
    return [
      {
        key: slot.slotId,
        channel: p.channel,
        value: p.presence === 'present' && typeof slot.value === 'string' ? slot.value : null,
        defaultKey: p.effectiveDefault,
        declared: p.allowedColourTokens,
        clearable: slot.clearable && p.presence === 'present',
      },
    ];
  });
}

/** The number format control's state for one slot, or null when it has none to edit. */
export function formatFromSlot(slot: DetailSlot): {
  base: NumberFormat | 'scalar-native';
  effectiveDefault: NumberFormat | 'scalar-native';
  seed: NumberFormat | null;
  isDefault: boolean;
  unsupported: boolean;
  valueType: FormatValueType;
} | null {
  const p = slot.presentation;
  if (p?.kind !== 'numberFormat' || slot.property !== 'numberFormat') return null;
  const present = p.presence === 'present' && typeof slot.value === 'object';
  return {
    base: present ? (slot.value as NumberFormat) : p.effectiveDefault,
    // What a Reset leaves: the registered default (a typed format, or the scalar's own).
    effectiveDefault: p.effectiveDefault,
    // INS's formatSeed (377e061db): where an edit starts; the default and oldValue stay
    // scalar-native.
    seed: p.formatSeed ?? null,
    isDefault: !present,
    // An unsupported legacy format stays visible and read-only; INS refuses it at prepare.
    unsupported: p.legacy === 'unsupported' || !slot.editable,
    valueType: p.valueType,
  };
}

/** The controls' choices as INS set Changes, each bound to its slot's current value. */
export function changesFor(
  slots: readonly DetailSlot[],
  picked: readonly PresentationChange[],
): Change[] {
  const byId = new Map(slots.map((s) => [s.slotId, s]));
  return picked.flatMap((pick) => {
    const slot = byId.get(pick.key);
    if (!slot?.editable) return [];
    if ('clear' in pick) return slot.clearable ? [clearChange(slot)] : [];
    if (slot.value === pick.value) return [];
    return [
      { slotId: slot.slotId, property: slot.property, oldValue: slot.value, newValue: pick.value },
    ];
  });
}

/** INS's clear arm: remove the stored override; the server binds its presence and restore. */
export const clearChange = (slot: DetailSlot): Change => ({
  slotId: slot.slotId,
  property: slot.property,
  oldValue: slot.value,
  action: 'clear-override',
});

/** A typed format for a number format slot, as one INS set Change. */
export function formatChange(slot: DetailSlot, next: NumberFormat): Change[] {
  if (!slot.editable || slot.presentation?.kind !== 'numberFormat') return [];
  return [{ slotId: slot.slotId, property: 'numberFormat', oldValue: slot.value, newValue: next }];
}

// ------------------------------------------------------------ across a selection
// The controls render from the first target's slots. The same slot on every other selected
// target is the one with the same presentation key (colour channel, spacing side, the number
// format); each target's change carries its own current value. A target without that editable
// slot makes the whole change refuse, never a partial one (ART refuses uncovered targets too).

/** The slot's identity across targets: its property and its channel or side. */
export function slotKey(slot: DetailSlot): string | null {
  const p = slot.presentation;
  if (!p || p.kind !== slot.property) return null;
  if (p.kind === 'colour') return `colour:${p.channel}`;
  if (p.kind === 'spacing') return `spacing:${p.side}`;
  return p.kind;
}

const sameValue = (a: unknown, b: unknown) =>
  typeof a === 'object' && a && typeof b === 'object' && b
    ? sameFormat(a as NumberFormat, b as NumberFormat)
    : a === b;

/** Keys whose slots differ across the selection (or are missing somewhere): shown as Mixed. */
export function mixedKeys(selection: readonly (readonly DetailSlot[])[]): Set<string> {
  const mixed = new Set<string>();
  const [first, ...rest] = selection;
  for (const slot of first ?? []) {
    const key = slotKey(slot);
    if (!key) continue;
    for (const other of rest) {
      const match = other.find((s) => slotKey(s) === key);
      if (!match || !sameValue(match.value, slot.value)) mixed.add(key);
    }
  }
  return mixed;
}

/** One control choice (keyed by the first target's slot ids) as Changes for every target. */
export function changesAcross(
  selection: readonly (readonly DetailSlot[])[],
  picked: readonly PresentationChange[],
): Change[] | null {
  const first = new Map((selection[0] ?? []).map((s) => [s.slotId, s]));
  const out: Change[] = [];
  for (const pick of picked) {
    const lead = first.get(pick.key);
    const k = lead && slotKey(lead);
    if (!k) return null;
    const each: Change[] = [];
    for (const slots of selection) {
      const slot = slots.find((s) => slotKey(s) === k);
      if (!slot?.editable) return null;
      // Reset clears the override on every target; one without a clearable override refuses it.
      if ('clear' in pick) {
        if (!slot.clearable) return null;
        each.push(clearChange(slot));
      } else
        each.push({
          slotId: slot.slotId,
          property: slot.property,
          oldValue: slot.value,
          newValue: pick.value,
        });
    }
    // The whole selection or nothing: when any target changes, every selected target takes part
    // (one already at the value too, so a mixed selection unifies); when none does, no change.
    if (each.some((c) => !('newValue' in c) || c.newValue !== c.oldValue)) out.push(...each);
  }
  return out;
}

/**
 * The pending number format choices, applied in order to each target's own format (or its seed
 * when none is stored), as one Change per target that ends up different.
 */
export function formatChangesAcross(
  selection: readonly (readonly DetailSlot[])[],
  choices: readonly (readonly [FormatGroup, string])[],
): Change[] | null {
  const out: Change[] = [];
  let changes = false;
  for (const slots of selection) {
    const slot = slots.find((s) => slotKey(s) === 'numberFormat');
    const state = slot && slot.editable ? formatFromSlot(slot) : null;
    if (!slot || !state || state.unsupported) return null;
    const base = state.base === 'scalar-native' ? state.seed : state.base;
    if (!base) return null;
    const next = choices.reduce((f, [group, choice]) => withChoice(f, group, choice), base);
    if (state.base === 'scalar-native' || !sameFormat(next, base)) changes = true;
    out.push(...formatChange(slot, next));
  }
  // Every selected target takes part when any changes (a mixed selection unifies); else nothing.
  return changes ? out : [];
}

/** Reset for the number format on every selected target, or a refusal if one can't be cleared. */
export function formatClearAcross(selection: readonly (readonly DetailSlot[])[]): Change[] | null {
  const out: Change[] = [];
  for (const slots of selection) {
    const slot = slots.find((s) => slotKey(s) === 'numberFormat');
    if (!slot?.editable || !slot.clearable) return null;
    out.push(clearChange(slot));
  }
  return out;
}

// ----------------------------------------------------------------------- images (#3365)
// imageFit and imageRatio are the image node's registered presentation slots (ARCH-IMAGE-SLOT-
// PRESENTATION). Fit is read-only unless the image is available in every selected context; cover
// is offered only when every selected slot is crop-safe. Ratio offers only ratios every selected
// slot allows.

/** The fit control's state across the selection, or null when a target has no fit slot. */
export function imageFitAcross(selection: readonly (readonly DetailSlot[])[]): {
  value: ImageFit | null;
  defaultFit: ImageFit;
  available: boolean;
  cropSafe: boolean;
  mixed: boolean;
  lead: DetailSlot;
} | null {
  const slots = selection.map((s) => s.find((x) => x.presentation?.kind === 'imageFit'));
  if (!slots.length || slots.some((x) => !x || !x.editable)) return null;
  const all = slots as DetailSlot[];
  const meta = all.map((x) => x.presentation as Extract<SlotPresentation, { kind: 'imageFit' }>);
  const values = all.map((x, i) => (meta[i].presence === 'present' ? (x.value as ImageFit) : null));
  return {
    value: values[0],
    defaultFit: meta[0].effectiveDefault,
    available: meta.every((m) => m.available),
    cropSafe: meta.every((m) => m.cropSafe),
    mixed: all.some((x) => x.value !== all[0].value),
    lead: all[0],
  };
}

/** The ratio control's state across the selection, or null when a target has no ratio slot. */
export function imageRatioAcross(selection: readonly (readonly DetailSlot[])[]): {
  value: ImageRatio | null;
  defaultRatio: ImageRatio;
  allowed: ImageRatio[];
  mixed: boolean;
  lead: DetailSlot;
} | null {
  const slots = selection.map((s) => s.find((x) => x.presentation?.kind === 'imageRatio'));
  if (!slots.length || slots.some((x) => !x || !x.editable)) return null;
  const all = slots as DetailSlot[];
  const meta = all.map((x) => x.presentation as Extract<SlotPresentation, { kind: 'imageRatio' }>);
  return {
    value: meta[0].presence === 'present' ? (all[0].value as ImageRatio) : null,
    defaultRatio: meta[0].effectiveDefault,
    allowed: meta[0].allowedRatios.filter((r) => meta.every((m) => m.allowedRatios.includes(r))),
    mixed: all.some((x) => x.value !== all[0].value),
    lead: all[0],
  };
}
